/**
 * Claims API
 *
 * Handles fetching claim data and marking claims as completed.
 */

import { apiClient } from './client';

// Types
export type ClaimStatus = 'NOT_ELIGIBLE' | 'PENDING_ROOT' | 'CLAIMABLE' | 'CLAIMED';

export interface ClaimData {
  canClaim: boolean;
  claimStatus: ClaimStatus;
  challengeAddress: string;
  proof: string[];
  index: number;
  amount: string;
  amountWei: string;
  isOffChain: boolean;
}

interface ClaimDataResponse {
  success: boolean;
  claimData: ClaimData;
}

interface ClaimOffChainResponse {
  success: boolean;
  message: string;
  txHash: string;
  amount: number;
}

// API methods
export const claimsApi = {
  /**
   * Get claim data for a challenge
   */
  getClaimData: async (challengeId: string): Promise<ClaimData> => {
    const response = await apiClient.get<ClaimDataResponse>(
      `/challenges/${challengeId}/claim-data`
    );
    return response.data.claimData;
  },

  /**
   * Mark a claim as completed after successful on-chain transaction
   */
  markClaimed: async (challengeId: string, txHash: string): Promise<void> => {
    await apiClient.post(`/challenges/${challengeId}/claim`, { txHash });
  },

  /**
   * Claim rewards for off-chain (marathon) challenges
   * Backend transfers USDC directly from admin wallet
   */
  claimOffChain: async (challengeId: string): Promise<{ txHash: string; amount: number }> => {
    const response = await apiClient.post<ClaimOffChainResponse>(
      `/challenges/${challengeId}/claim-offchain`
    );
    return { txHash: response.data.txHash, amount: response.data.amount };
  },
};
